/**
 * Helper functions for handling autocomplete data
 */

import { isValidLocationType, LocationType, LocationTypeEnum } from '@app/enums';
import { AutocompleteItemModel } from '@app/models/elastic-search';

export interface GeoLocationData {
    geoId: string;
    locationType: LocationType;
}

/**
 * Extracts geoId and type from an autocomplete item's ID field
 * @param item The autocomplete item
 * @returns Object with geoId and type, or null if parsing fails
 */
export function extractGeoIdAndTypeFromAutocompleteItem(
    item: AutocompleteItemModel | null | undefined,
): GeoLocationData | null {
    if (!item?.id) {
        return null;
    }

    const parts = item.id.split('/');
    if (parts.length !== 2) {
        return null;
    }

    const [type, geoId] = parts;

    if (!isValidLocationType(type)) {
        return null;
    }

    const pattern = type === LocationTypeEnum.POI ? /^NOC_\d+$/ : /^\d+$/;

    if (!pattern.test(geoId)) {
        return null;
    }

    return {
        geoId,
        locationType: type as LocationType,
    };
}

/**
 * Finds an autocomplete item by name in a list of items
 * @param items List of autocomplete items
 * @param name Name to search for
 * @returns The matching item or null if not found
 */
export function findAutocompleteItemByName(
    items: AutocompleteItemModel[] | null,
    name: string,
): AutocompleteItemModel | null {
    if (!items || !name) {
        return null;
    }

    return items.find(item => item.name === name) || null;
}
